import { LOAD_EFFECTS } from "../../content/loads.js";
import type { StageDefinition } from "../../content/stages.js";
import type { CaseDefinition, LoadEffect, NodeDefinition } from "../../domain/types/index.js";
import type { EditorNode } from "../stageEditor/mapEdit.js";
import { normalizeStartDeck } from "../stageEditor/startDeck.js";
import { toStageOrgPool, toStageRewardPools } from "../stageEditor/storedRewards.js";
import type { StoredStage } from "./stageRecord.js";

function loadsOf(node: EditorNode): LoadEffect[] {
  const mapped = node.loads.flatMap((entry) => {
    const base = LOAD_EFFECTS[entry.id as keyof typeof LOAD_EFFECTS];
    return base ? [{ ...base, weight: entry.weight }] : [];
  });
  if (mapped.length > 0) return mapped;
  return [{ ...LOAD_EFFECTS.interrupt, weight: 50 }, { ...LOAD_EFFECTS["rework-ticket"], weight: 50 }];
}

function stageStart(record: StoredStage): Partial<StageDefinition> {
  if (!record.startDeck) return {};
  const deck = normalizeStartDeck(record.startDeck);
  return {
    startingDeck: { intern: deck.intern, idle: deck.idle, hedge: deck.hedge },
    allowBringIn: deck.allowBringIn,
  };
}

function stageRewards(record: StoredStage): Partial<StageDefinition> {
  const rewardPools = toStageRewardPools(record.rewards);
  const orgRewardPool = toStageOrgPool(record.rewards);
  return {
    ...(rewardPools ? { rewardPools } : {}),
    ...(orgRewardPool ? { orgRewardPool } : {}),
  };
}

export function toPlayable(record: StoredStage): { stage: StageDefinition; cases: CaseDefinition[] } {
  const nodes: Record<string, NodeDefinition> = {};
  const cases: CaseDefinition[] = [];
  for (const node of Object.values(record.map.nodes)) {
    const caseId = node.kind === "case" ? `${record.id}:${node.id}` : undefined;
    nodes[node.id] = { id: node.id, kind: node.kind, next: node.next, x: node.x, y: node.y, ...(caseId ? { caseId } : {}) };
    if (caseId) {
      cases.push({
        id: caseId,
        name: node.name || node.id,
        size: node.size,
        required: node.required,
        deadline: node.deadline,
        flavor: node.flavor,
        loads: loadsOf(node),
        ...(node.size === "boss" ? { isBoss: true } : {}),
        ...(node.size === "large" ? { grantsOrganization: true } : {}),
      });
    }
  }
  return {
    stage: {
      id: record.id,
      name: record.name,
      difficulty: record.difficulty,
      flavor: record.flavor,
      nodes,
      ...stageRewards(record),
      ...stageStart(record),
    },
    cases,
  };
}
